// ============================================================================
// ConfirmDialog — styled confirmation for destructive team actions
// (ORG-USAGE-2026 roles UX revamp)
//
// Replaces window.confirm/alert on the team surfaces. A native confirm can't
// explain consequences, can't be branded, and reads as a browser error to
// anyone who isn't expecting it — which is exactly the wrong tone for
// "you are about to remove a colleague".
// ============================================================================

import React from 'react';
import styled from 'styled-components';
import Modal from '../common/Modal';

const Body = styled.div`
  padding: 0.25rem 0 0.5rem;

  p {
    color: #444;
    font-size: 0.9375rem;
    line-height: 1.6;
    margin: 0 0 0.75rem;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const Consequence = styled.div`
  background: #faf8f3;
  border: 1px solid #ece4d3;
  border-radius: 8px;
  padding: 0.75rem 0.875rem;
  color: #6b5d3f;
  font-size: 0.8125rem;
  line-height: 1.55;
  margin-top: 0.75rem;
`;

const DialogError = styled.div`
  margin-top: 0.875rem;
  border: 1px solid rgba(153, 27, 27, 0.25);
  background: rgba(153, 27, 27, 0.05);
  color: #991b1b;
  border-radius: 8px;
  padding: 0.625rem 0.75rem;
  font-size: 0.8125rem;
  line-height: 1.5;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.625rem;
`;

const CancelBtn = styled.button`
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  color: #666;
  cursor: pointer;
  text-transform: lowercase;

  &:hover {
    background: #f5f5f5;
  }
`;

const ConfirmBtn = styled.button<{ $danger: boolean }>`
  background: ${(p) => (p.$danger ? '#991B1B' : 'var(--brand-gold)')};
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  color: #fff;
  cursor: pointer;
  text-transform: lowercase;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

interface Props {
  isOpen: boolean;
  title: string;
  message: React.ReactNode;
  /** What happens as a result — shown in a highlighted note */
  consequence?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  busy?: boolean;
  /**
   * Failure from the last attempt. Shown INSIDE the dialog: the dialog is a
   * fixed full-screen overlay, so an error banner on the page behind it is
   * invisible and the action just looks like it did nothing.
   */
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<Props> = ({
  isOpen,
  title,
  message,
  consequence,
  confirmLabel = 'confirm',
  cancelLabel = 'cancel',
  danger = false,
  busy = false,
  error = null,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      maxWidth="440px"
      footer={
        <Footer>
          <CancelBtn onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </CancelBtn>
          <ConfirmBtn $danger={danger} onClick={onConfirm} disabled={busy}>
            {busy ? 'working…' : error ? 'try again' : confirmLabel}
          </ConfirmBtn>
        </Footer>
      }
    >
      <Body>
        {typeof message === 'string' ? <p>{message}</p> : message}
        {consequence && <Consequence>{consequence}</Consequence>}
        {error && <DialogError role="alert">{error}</DialogError>}
      </Body>
    </Modal>
  );
};

export default ConfirmDialog;
